/**
 * Utilidades para trabajar con tokens JWT.
 * @module jwt.util
 */

import { logger } from './logger.util';

/**
 * Decodifica un JWT y extrae el payload.
 * @param token - Token JWT a decodificar
 * @returns Payload decodificado o null si hay un error
 */
export function decodeJWT(token: string): Record<string, any> | null {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replaceAll('-', '+').replaceAll('_', '/'));
    return JSON.parse(decoded);
  } catch (error) {
    logger.debug('[JWTUtil] Error al decodificar JWT', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

/**
 * Obtiene el timestamp de expiración (exp) de un JWT.
 * @param token - Token JWT
 * @returns Timestamp de expiración en segundos o null
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp;
}

/**
 * Calcula los segundos restantes hasta que el token expira.
 * @param token - Token JWT
 * @returns Segundos restantes o 0 si ha expirado
 */
export function getTokenTimeRemaining(token: string): number {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const remaining = expiration - now;

  return Math.max(remaining, 0);
}

